import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { ChordDiagramProps } from "./ChartTypes";

const ChordDiagram: React.FC<ChordDiagramProps> = ({
  parentid,
  protocolMatrixData,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current).selectAll("*").remove();

    const parentElement = document.getElementById(parentid);
    if (!parentElement) {
      // Handle the error
      console.error(`No element with ID ${parentid}`);
      return;
    }
    const rect = parentElement.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const svg = d3.select(svgRef.current).attr("width", w).attr("height", h);

    if (protocolMatrixData) {
      //show chord graph protocol port mapping for the host

      const graphwidth = w,
        graphheight = h,
        outerRadius = Math.min(graphwidth, graphheight) / 2 - 10,
        innerRadius = outerRadius - 24;

      // const formatPercent = d3.format(".1%");

      const arc = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

      const layout = d3
        .chord()
        .padAngle(0.04)
        .sortSubgroups(d3.descending)
        .sortChords(d3.ascending);

      // const path = d3.chord()
      // .radius(innerRadius);
      const path = d3
        .ribbon()
        .radius(innerRadius - 1)
        .padAngle(1 / innerRadius);

      svg
        .append("g")
        .attr("id", "circle")
        .attr(
          "transform",
          "translate(" + graphwidth / 2 + "," + graphheight / 2 + ")",
        );

      svg
        .append("circle")
        .attr("r", outerRadius)
        .attr("class", "translate-y-1/2 translate-x-1/2")
        .attr("fill", "var(--primary-background)");

      //d3.csv("teams.csv", function(cities) {
      const matrix: number[][] = [];
      let matrixRow: number[] = [];

      let portprotocol = protocolMatrixData.protocols; //list of protocols
      portprotocol = portprotocol.concat(protocolMatrixData.ports);
      //console.log(portprotocol);
      //console.log(protocolMatrixData.protocolMatrix.ports);
      const chordLength = portprotocol.length;
      //making a square matrix
      for (let i = 0; i < chordLength; i++) {
        for (let j = 0; j < chordLength; j++) {
          matrixRow.push(0);
        }
        matrix.push(matrixRow);
        matrixRow = [];
      }
      //console.log(matrix);
      const protocolMatrix = protocolMatrixData.matrix;
      for (let i = 0; i < protocolMatrix.length; ++i) {
        const splitResult = protocolMatrix[i].split(":");
        // const protocolIndex = $.inArray(splitResult[0], portprotocol)
        const protocolIndex = portprotocol.indexOf(splitResult[0]);
        // const portIndex = $.inArray(splitResult[1], portprotocol)
        const portIndex = portprotocol.indexOf(splitResult[1]);
        matrix[protocolIndex][portIndex] = 1;
        matrix[portIndex][protocolIndex] = 1;
      }

      // Compute the chord layout.
      // layout.matrix(matrix);
      const chords = layout(matrix);

      const divTooltip = d3
        .select("body")
        .append("div")
        .attr(
          "class",
          "toolTip-mc absolute z-modal-level-3 bg-primary-foreground text-primary p-1",
        );
      const mouseover = (d: any, i: any) => {
        // console.log('here in mouseover',d,portprotocol[i.index]);

        // chord.classed("hidden", function (p: any) {
        //     return p.source.index != i.index && p.target.index != i.index;
        // });

        // const allchords = d3.selectAll(".chordpath");
        // allchords.transition().duration(100).style("opacity", 0);

        // const size = allchords.nodes().length;
        // d3.select(allchords?.nodes()[i.index < size ? i.index : i.index - size])
        //   .transition()
        //   .duration(100)
        //   .delay(100)
        //   .style("opacity", 0.5);

        const allchords = d3.selectAll(".chordpath");
        allchords.classed("!opacity-0", function (p: any) {
          return p.source.index != i.index && p.target.index != i.index;
        });

        // place tooltip near the arc
        divTooltip.style("left", d.pageX + 10 + "px");
        divTooltip.style("top", d.pageY - 25 + "px");
        divTooltip.style("display", "inline-block");
        divTooltip.html(portprotocol[i.index]);
      };

      const mouseout = () => {
        const allchords = d3.selectAll(".chordpath");
        allchords.classed("!opacity-0", false);
        allchords.transition().duration(100).style("opacity", 0.5);
      };

      // Add a group per neighborhood.
      const group = svg
        .selectAll(".group")
        .data(chords.groups)
        .enter()
        .append("g")
        .attr("class", "group")
        // .attr("transform", "translate(50%, 50%)")
        .on("mouseover", mouseover)
        // .on("mouseout", function (_d) {
        //     divTooltip.style("display", "none");
        // });;
        .on("mouseout", mouseout);

      // Add a mouseover title.
      // group.append("title").text(function(d, i) {
      // return cities[i].name + ": " + formatPercent(d.value) + " of origins";
      // });

      //for random color
      // const color = d3.scale.category20();
      const color = d3
        .scaleOrdinal<string>()
        .domain(portprotocol)
        .range(d3.schemeCategory10);

      // Add the group arc.
      group
        .append("path")
        .attr("id", function (_, i) {
          return "group" + i;
        })
        .attr("class", "translate-x-1/2 translate-y-1/2")
        .attr("d", arc as any)
        .style("cursor", "pointer")
        .style("fill", function (_, i) {
          return color(portprotocol[i]);
        });

      // Add a text label.
      const groupText = group
        .append("text")
        .attr("x", 6)
        .attr("dy", 15)
        .style("font-size", 11)
        .style("cursor", "pointer")
        .attr("fill", "currentColor");

      groupText
        .append("textPath")
        .attr("xlink:href", function (_d, i) {
          return "#group" + i;
        })
        .text(function (_d, i) {
          // console.log(groupText.nodes()[i].getComputedTextLength())
          // limit the char lenght to 5 characters
          if (portprotocol[i].length > 5) {
            return portprotocol[i].substring(0, 5)?.toLowerCase() + "..";
          }
          return portprotocol[i]?.toLowerCase();
        });

      // Remove the labels that don't fit. :(
      // groupText.filter(function (d, i) { return groupPath[0][i].getTotalLength() / 2 - 16 < this.getComputedTextLength(); })
      //     .remove();

      // Add the chords.
      svg
        .selectAll(".chord")
        .data(chords)
        .enter()
        .append("path")
        .style("opacity", 0.5)
        .attr("class", "translate-x-1/2 translate-y-1/2 chordpath")
        .style("fill", function (_d, i) {
          return color(portprotocol[i]);
        })
        .attr("d", path as any);

      // Add an elaborate mouseover title for each chord.
      /*chord.append("title").text(function(d) {
            return portprotocol[d.source.index].name
            + " → " + portprotocol[d.target.index].name
            + ": " + formatPercent(d.source.value)
            + "\n" + portprotocol[d.target.index].name
            + " → " + portprotocol[d.source.index].name
            + ": " + formatPercent(d.target.value);
            });*/
    }

    return () => {
      // remove tooltip element
      const divTooltips = d3.select("body").selectAll(".toolTip-mc");
      divTooltips.remove();
    };
  }, [protocolMatrixData]);

  return <svg ref={svgRef}></svg>;
};

export default ChordDiagram;
